import axios from 'axios';

// Get the API URL from environment variables or use a default
const API_URL = import.meta.env.VITE_API_URL 
  ? `${import.meta.env.VITE_API_URL}`
  : 'http://localhost:3001';

// Cache store for API responses - separate caches for partner mode and non-partner mode
const cache = {
  savings: null,
  savingsStats: null,
  lastFetchTime: 0,
  cacheExpiry: 60000, // Cache expires after 1 minute
  // Partner mode cache
  partnerSavings: null,
  partnerSavingsStats: null,
  partnerLastFetchTime: 0
};

/**
 * Savings Service
 * 
 * Handles all savings-related operations and data fetching
 */
const savingsService = {
  /**
   * Clear all caches (useful when switching between partner/personal modes)
   */
  clearCache() {
    cache.savings = null;
    cache.savingsStats = null;
    cache.partnerSavings = null;
    cache.partnerSavingsStats = null;
    cache.lastFetchTime = 0;
    cache.partnerLastFetchTime = 0;
    console.log('🗑️ Savings service cache cleared');
  },

  /**
   * Get authentication header with JWT token
   * @returns {Object} Headers object with Authorization
   */
  getAuthHeader() {
    const token = localStorage.getItem('token');
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    };
  },

  /**
   * Get all savings accounts for the current user with caching
   * @returns {Promise} Promise with savings data
   */
  async getAllSavings(isPartnerMode = false, isOurMode = false) {
    try {
      // Check if we have cached data and if it's still valid - use mode-specific cache
      const now = Date.now();
      let cachedData, lastFetch;
      const mode = isOurMode ? 'our' : (isPartnerMode ? 'partner' : 'personal');
      
      if (isOurMode) {
        cachedData = cache.ourSavings;
        lastFetch = cache.ourLastFetchTime;
      } else if (isPartnerMode) {
        cachedData = cache.partnerSavings;
        lastFetch = cache.partnerLastFetchTime;
      } else {
        cachedData = cache.savings;
        lastFetch = cache.lastFetchTime;
      }
      
      const cacheAge = now - lastFetch;
      const hasValidCache = cachedData && (cacheAge < cache.cacheExpiry);
      
      console.log('🔍 Cache status:', {
        hasCachedData: !!cachedData,
        cacheAge: cacheAge,
        cacheExpiry: cache.cacheExpiry,
        isValid: hasValidCache,
        mode
      });
      
      if (hasValidCache) {
        console.log(`Using cached savings data (${mode})`);
        return cachedData;
      }

      // Fetch both manual savings goals and Wallet savings accounts
      let savingsUrl = `${API_URL}/api/cashflow/savings`;
      if (isPartnerMode) {
        savingsUrl += `?partner=true`;
      } else if (isOurMode) {
        savingsUrl += `?includePartner=true`;
      }
      
      const [manualSavingsResponse, walletSavingsResponse] = await Promise.all([
        axios.get(savingsUrl, this.getAuthHeader()).catch(error => {
          console.warn('Manual savings not available:', error.message);
          return { data: [] };
        }),
        this.getWalletSavingsAccounts(isPartnerMode, isOurMode).catch(error => {
          console.warn('Wallet savings accounts not available:', error.message);
          return [];
        })
      ]);

      // Debug: Log the raw response to understand its structure
      console.log('🔍 Manual savings raw response:', manualSavingsResponse);
      console.log('🔍 Manual savings response.data:', manualSavingsResponse.data);

      // Combine manual savings goals with wallet accounts
      // Extract the actual data array from the server response
      const manualSavingsData = manualSavingsResponse.data?.data || manualSavingsResponse.data || [];
      const manualSavings = Array.isArray(manualSavingsData) ? manualSavingsData : [];
      const walletSavings = Array.isArray(walletSavingsResponse) ? walletSavingsResponse : [];
      
      console.log('🔍 Extracted manual savings data:', manualSavingsData);
      console.log('🔍 Manual savings count:', manualSavings.length);
      
      console.log('💰 Savings Integration:', {
        manualSavings: manualSavings.length,
        walletSavings: walletSavings.length,
        total: manualSavings.length + walletSavings.length
      });

      // Combine both types
      const allSavings = [...manualSavings, ...walletSavings];
      
      // Update mode-specific cache
      if (isPartnerMode) {
        cache.partnerSavings = allSavings;
        cache.partnerLastFetchTime = now;
      } else {
        cache.savings = allSavings;
        cache.lastFetchTime = now;
      }
      
      return allSavings;
    } catch (error) {
      console.error('Error fetching savings:', error);
      // If we have cached data, return it even if expired
      const fallbackCache = isPartnerMode ? cache.partnerSavings : cache.savings;
      if (fallbackCache) {
        console.log(`Using expired cached data for savings (${isPartnerMode ? 'partner' : 'personal'})`);
        return fallbackCache;
      }
      throw error;
    }
  },

  /**
   * Get savings accounts from Wallet (both manual and Plaid)
   * @returns {Promise<Array>} Array of wallet savings accounts
   */
  async getWalletSavingsAccounts(isPartnerMode = false, isOurMode = false) {
    try {
      const mode = isOurMode ? 'our' : (isPartnerMode ? 'partner' : 'personal');
      console.log(`🏦 Fetching Wallet savings accounts (${mode})...`);
      
      // Use the full API URL to ensure it goes to the API server, not the client domain
      let url = `${API_URL}/api/wallet/savings-accounts`;
      if (isPartnerMode) {
        url += `?partner=true`;
      } else if (isOurMode) {
        url += `?includePartner=true`;
      }
      
      const response = await axios.get(url, this.getAuthHeader());
      
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        const accounts = response.data.data;
        
        // Transform to match expected format for statistics
        const walletSavingsAccounts = accounts.map(account => ({
          _id: account._id,
          name: account.bankName || account.name,
          type: account.accountType || 'Savings',
          currentAmount: account.balance || 0,
          targetAmount: null, // Wallet accounts don't have target amounts
          contributionAmount: null,
          frequency: null,
          interestRate: account.interestRate || 0,
          status: 'active',
          accountSource: account.isPlaidAccount ? 'plaid' : 'wallet',
          institution: account.institutionName || account.bankName || 'Manual Entry',
          lastUpdated: account.lastUpdated || new Date(),
          isWalletAccount: true,
          isPlaidAccount: account.isPlaidAccount || false,
          originalAccount: account
        }));

        console.log('💰 Wallet Savings Accounts Found (new endpoint):', {
          totalAccounts: accounts.length,
          savingsFound: walletSavingsAccounts.length,
          accounts: walletSavingsAccounts.map(acc => ({
            name: acc.name,
            type: acc.type,
            amount: acc.currentAmount,
            source: acc.accountSource
          }))
        });

        return walletSavingsAccounts;
      } else {
        console.warn('Unexpected response format from savings accounts endpoint');
        return [];
      }
    } catch (error) {
      console.error('❌ Error fetching wallet savings accounts from new endpoint:', error);
      
      // Fallback to old method if new endpoint fails
      console.log('🔄 Falling back to old method...');
      try {
        // Get all wallet accounts (both manual and Plaid)
        const [regularAccountsResponse, plaidAccountsResponse] = await Promise.all([
          // Get manual wallet accounts
          axios.get(`${API_URL}/api/wallet`, this.getAuthHeader()).catch(error => {
            console.warn('Regular accounts not available:', error.message);
            return { data: { success: true, data: [] } };
          }),
          // Get Plaid accounts
          axios.get(`${API_URL}/api/plaid/accounts`, this.getAuthHeader()).catch(error => {
            console.warn('Plaid accounts not available:', error.message);
            return { data: [] };
          })
        ]);

        const walletSavingsAccounts = [];
        
        // Process regular wallet accounts
        const regularAccounts = regularAccountsResponse.data?.data || [];
        regularAccounts.forEach(account => {
          const accountType = (account.accountType || account.type || '').toLowerCase();
          
          // Include savings, retirement, and investment account types
          if (this.isSavingsAccount(accountType, account.name)) {
            walletSavingsAccounts.push({
              _id: `wallet_${account._id}`,
              name: account.name || account.bankName,
              type: this.mapAccountTypeToSavingsType(accountType, account.name),
              currentAmount: account.balance || 0,
              targetAmount: null, // Wallet accounts don't have target amounts
              contributionAmount: null,
              frequency: null,
              interestRate: account.interestRate || 0,
              status: 'active',
              accountSource: 'wallet',
              institution: account.bankName || account.institutionName || 'Manual Entry',
              lastUpdated: account.lastUpdated || new Date(),
              isWalletAccount: true,
              originalAccount: account
            });
          }
        });

        // Process Plaid accounts
        const plaidAccounts = Array.isArray(plaidAccountsResponse.data) ? plaidAccountsResponse.data : [];
        plaidAccounts.forEach(account => {
          const accountType = (account.type || '').toLowerCase();
          const subtype = (account.subtype || '').toLowerCase();
          
          // Include depository (savings), investment, and retirement accounts
          if (this.isSavingsPlaidAccount(accountType, subtype, account.name)) {
            walletSavingsAccounts.push({
              _id: `plaid_${account._id}`,
              name: account.name,
              type: this.mapPlaidAccountTypeToSavingsType(accountType, subtype, account.name),
              currentAmount: account.currentBalance || account.availableBalance || 0,
              targetAmount: null, // Plaid accounts don't have target amounts
              contributionAmount: null,
              frequency: null,
              interestRate: 0, // Plaid doesn't provide interest rates for most accounts
              status: 'active',
              accountSource: 'plaid',
              institution: account.itemId?.institutionName || 'Connected Bank',
              lastUpdated: account.lastUpdated || new Date(),
              isPlaidAccount: true,
              isWalletAccount: true,
              originalAccount: account
            });
          }
        });

        console.log('💰 Wallet Savings Accounts Found (fallback method):', {
          regular: regularAccounts.length,
          plaid: plaidAccounts.length,
          savingsFound: walletSavingsAccounts.length,
          accounts: walletSavingsAccounts.map(acc => ({
            name: acc.name,
            type: acc.type,
            amount: acc.currentAmount,
            source: acc.accountSource
          }))
        });

        return walletSavingsAccounts;
      } catch (fallbackError) {
        console.error('❌ Fallback method also failed:', fallbackError);
        return [];
      }
    }
  },

  /**
   * Check if an account type represents a savings account
   * @param {string} accountType - Account type
   * @param {string} accountName - Account name for additional context
   * @returns {boolean} True if it's a savings account
   */
  isSavingsAccount(accountType, accountName) {
    const savingsTypes = [
      'savings', 'saving', 'money market', 'cd', 'certificate of deposit',
      'retirement', '401k', '403b', 'ira', 'roth', 'pension',
      'investment', 'brokerage', 'mutual fund', 'stocks', 'bonds'
    ];
    
    const lowerType = accountType.toLowerCase();
    const lowerName = (accountName || '').toLowerCase();
    
    return savingsTypes.some(type => 
      lowerType.includes(type) || lowerName.includes(type)
    );
  },

  /**
   * Check if a Plaid account represents a savings account
   * @param {string} type - Plaid account type
   * @param {string} subtype - Plaid account subtype
   * @param {string} name - Account name
   * @returns {boolean} True if it's a savings account
   */
  isSavingsPlaidAccount(type, subtype, name) {
    // Include depository accounts that are savings-related
    if (type === 'depository') {
      const savingsSubtypes = ['savings', 'money market', 'cd', 'certificate of deposit'];
      return savingsSubtypes.includes(subtype) || 
             (name && this.isSavingsAccount(name, name));
    }
    
    // Include investment accounts
    if (type === 'investment') {
      return true;
    }
    
    // Include retirement accounts (might come through as depository or investment)
    const retirementSubtypes = ['401k', '403b', 'ira', 'roth', 'pension', 'retirement'];
    return retirementSubtypes.includes(subtype) ||
           (name && retirementSubtypes.some(ret => name.toLowerCase().includes(ret)));
  },

  /**
   * Map wallet account type to savings category
   * @param {string} accountType - Original account type
   * @param {string} accountName - Account name for context
   * @returns {string} Mapped savings type
   */
  mapAccountTypeToSavingsType(accountType, accountName) {
    const lowerType = accountType.toLowerCase();
    const lowerName = (accountName || '').toLowerCase();
    
    if (lowerType.includes('retirement') || lowerName.includes('401k') || lowerName.includes('ira')) {
      return 'Retirement';
    } else if (lowerType.includes('investment') || lowerName.includes('brokerage')) {
      return 'Investment';
    } else if (lowerName.includes('emergency') || lowerName.includes('rainy day')) {
      return 'Emergency Fund';
    } else if (lowerType.includes('savings') || lowerType.includes('money market')) {
      return 'General Savings';
    } else {
      return 'Other';
    }
  },

  /**
   * Map Plaid account type/subtype to savings category
   * @param {string} type - Plaid account type
   * @param {string} subtype - Plaid account subtype
   * @param {string} name - Account name
   * @returns {string} Mapped savings type
   */
  mapPlaidAccountTypeToSavingsType(type, subtype, name) {
    const lowerName = (name || '').toLowerCase();
    
    // Check name for specific types first
    if (lowerName.includes('401k') || lowerName.includes('403b') || 
        lowerName.includes('ira') || lowerName.includes('retirement')) {
      return 'Retirement';
    } else if (lowerName.includes('emergency') || lowerName.includes('rainy day')) {
      return 'Emergency Fund';
    } else if (type === 'investment') {
      return 'Investment';
    } else if (subtype === 'savings') {
      return 'General Savings';
    } else if (subtype === 'money market') {
      return 'High Yield Savings';
    } else if (subtype === 'cd' || subtype === 'certificate of deposit') {
      return 'Certificate of Deposit';
    } else {
      return 'Other';
    }
  },

  /**
   * Get a savings account by its ID
   * @param {string} savingsId - The savings ID to retrieve
   * @returns {Promise} Promise with savings data
   */
  async getSavingsById(savingsId) {
    try {
      // First check if we have this savings account in our cache
      if (cache.savings) {
        const cachedSavings = cache.savings.find(saving => saving._id === savingsId);
        if (cachedSavings) {
          return cachedSavings;
        }
      }
      
              const response = await axios.get(`${API_URL}/cashflow/savings/${savingsId}`, this.getAuthHeader());
      return response.data;
    } catch (error) {
      console.error(`Error fetching savings ${savingsId}:`, error);
      throw error;
    }
  },

  /**
   * Create a new savings account
   * @param {Object} savingsData - The savings data to create
   * @returns {Promise} Promise with created savings data
   */
  async createSavings(savingsData) {
    try {
      const response = await axios.post(`${API_URL}/cashflow/savings`, savingsData, this.getAuthHeader());
      
      // Invalidate cache
      console.log('🗑️ Invalidating savings cache after creation');
      cache.savings = null;
      cache.savingsStats = null;
      cache.lastFetchTime = 0; // Force cache miss on next fetch
      
      // Return the actual savings data, not the wrapper object
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error creating savings account:', error);
      throw error;
    }
  },

  /**
   * Update a savings account
   * @param {string} savingsId - The savings ID to update
   * @param {Object} savingsData - The updated savings data
   * @returns {Promise} Promise with updated savings data
   */
  async updateSavings(savingsId, savingsData) {
    try {
      const response = await axios.put(
        `${API_URL}/cashflow/savings/${savingsId}`, 
        savingsData, 
        this.getAuthHeader()
      );
      
      // Invalidate cache
      cache.savings = null;
      cache.savingsStats = null;
      
      return response.data;
    } catch (error) {
      console.error(`Error updating savings ${savingsId}:`, error);
      throw error;
    }
  },

  /**
   * Delete a savings account
   * @param {string} savingsId - The savings ID to delete
   * @returns {Promise} Promise with deletion result
   */
  async deleteSavings(savingsId) {
    try {
      const response = await axios.delete(
        `${API_URL}/cashflow/savings/${savingsId}`, 
        this.getAuthHeader()
      );
      
      // Invalidate cache
      cache.savings = null;
      cache.savingsStats = null;
      
      return response.data;
    } catch (error) {
      console.error(`Error deleting savings ${savingsId}:`, error);
      throw error;
    }
  },

  /**
   * Record a contribution to a savings account
   * @param {string} savingsId - The savings ID
   * @param {Object} contributionData - Data about the contribution
   * @returns {Promise} Promise with updated savings data
   */
  async recordContribution(savingsId, contributionData) {
    try {
      const response = await axios.post(
        `${API_URL}/cashflow/savings/${savingsId}/contributions`,
        contributionData,
        this.getAuthHeader()
      );
      
      // Invalidate cache
      cache.savings = null;
      cache.savingsStats = null;
      
      return response.data;
    } catch (error) {
      console.error(`Error recording contribution to savings ${savingsId}:`, error);
      throw error;
    }
  },

  /**
   * Get savings statistics for dashboard with caching
   * @returns {Promise} Promise with savings stats data
   */
  async getSavingsStatistics(isPartnerMode = false, isOurMode = false) {
    try {
      // Check if we have cached stats and if they're still valid - use mode-specific cache
      const now = Date.now();
      let cachedStats, lastFetch;
      const mode = isOurMode ? 'our' : (isPartnerMode ? 'partner' : 'personal');
      
      if (isOurMode) {
        cachedStats = cache.ourSavingsStats;
        lastFetch = cache.ourLastFetchTime;
      } else if (isPartnerMode) {
        cachedStats = cache.partnerSavingsStats;
        lastFetch = cache.partnerLastFetchTime;
      } else {
        cachedStats = cache.savingsStats;
        lastFetch = cache.lastFetchTime;
      }
      
      if (cachedStats && (now - lastFetch < cache.cacheExpiry)) {
        console.log(`Using cached savings stats (${mode})`);
        return cachedStats;
      }

      // Fetch all savings accounts (this will use its own cache if available)
      let savingsAccounts = await this.getAllSavings(isPartnerMode, isOurMode);
      if (!Array.isArray(savingsAccounts)) savingsAccounts = [];
      
      if (savingsAccounts.length === 0) {
        const emptyStats = {
          totalSavings: 0,
          monthlySavingsRate: 0,
          annualReturnRate: 0,
          emergencyFundRatio: 0,
          goalProgress: 0,
          savingsToIncomeRatio: 0,
          projectedAnnualGrowth: 0,
          ytdContributions: 0
        };
        cache.savingsStats = emptyStats;
        return emptyStats;
      }
      
      // Calculate total savings amount (including wallet accounts)
      const totalSavings = savingsAccounts.reduce(
        (sum, account) => sum + (account.currentAmount || 0), 0
      );
      
      console.log('💰 Total Savings Calculation:', {
        totalAccounts: savingsAccounts.length,
        totalSavings: totalSavings,
        accountBreakdown: savingsAccounts.map(acc => ({
          name: acc.name,
          type: acc.type,
          amount: acc.currentAmount,
          source: acc.accountSource || 'manual'
        }))
      });
      
      // Calculate total target amount
      const totalTarget = savingsAccounts.reduce(
        (sum, account) => sum + (account.targetAmount || 0), 0
      );
      
      // Calculate monthly contribution rate from manual goals only
      // For wallet accounts, this would need to be calculated from transaction data
      const manualGoals = savingsAccounts.filter(account => account.accountSource !== 'plaid' && account.accountSource !== 'wallet');
      const monthlySavingsRate = manualGoals.reduce(
        (sum, account) => {
          if (account.frequency === 'monthly') {
            return sum + (account.contributionAmount || 0);
          } else if (account.frequency === 'weekly') {
            return sum + ((account.contributionAmount || 0) * 4.33);
          } else if (account.frequency === 'bi-weekly') {
            return sum + ((account.contributionAmount || 0) * 2.17);
          } else if (account.frequency === 'annually') {
            return sum + ((account.contributionAmount || 0) / 12);
          } else if (account.frequency === 'quarterly') {
            return sum + ((account.contributionAmount || 0) / 3);
          }
          return sum;
        }, 0
      );
      
      // TODO: For wallet accounts, calculate monthly savings rate from:
      // - Transaction data showing deposits to savings accounts
      // - Account balance changes over time
      console.log('📊 Monthly Rate Calculation: Using manual goals only. Wallet account contributions would need transaction analysis.');
      
      // Calculate average return rate
      let totalReturnRate = 0;
      let accountsWithRate = 0;
      
      savingsAccounts.forEach(account => {
        if (account.interestRate && account.interestRate > 0) {
          totalReturnRate += account.interestRate;
          accountsWithRate++;
        }
      });
      
      const annualReturnRate = accountsWithRate > 0 
        ? totalReturnRate / accountsWithRate 
        : 0;
      
      // Calculate emergency fund ratio (months of expenses covered)
      // Use actual expense data if available
      let monthlyExpenses = 0;
      let emergencyFundRatio = 0;
      
      try {
        // Try to get real expense data from the expense service
        const expenseResponse = await axios.get(`${API_URL}/api/cashflow/expenses`, this.getAuthHeader());
        if (expenseResponse.data && expenseResponse.data.monthlyTotal) {
          monthlyExpenses = expenseResponse.data.monthlyTotal;
        }
      } catch (error) {
        console.log('Could not fetch expense data for emergency fund calculation:', error.message);
        // For now, calculate based on income if expenses aren't available
        // Assume 70% of income as expenses (rough estimate)
        try {
          const incomeResponse = await axios.get(`${API_URL}/api/cashflow/income`, this.getAuthHeader());
          if (incomeResponse.data && Array.isArray(incomeResponse.data)) {
            const monthlyIncome = incomeResponse.data.reduce((sum, income) => {
              return sum + (income.monthlyAmount || 0);
            }, 0);
            monthlyExpenses = monthlyIncome * 0.7; // Estimate 70% of income as expenses
          }
        } catch (incomeError) {
          console.log('Could not fetch income data either:', incomeError.message);
          monthlyExpenses = 0;
        }
      }
      
      if (monthlyExpenses > 0) {
        // Find emergency fund accounts (accounts with "emergency" in name or type)
        const emergencyFunds = savingsAccounts.filter(account => 
          account.name?.toLowerCase().includes('emergency') || 
          account.type?.toLowerCase().includes('emergency')
        );
        
        const totalEmergencyFunds = emergencyFunds.reduce(
          (sum, account) => sum + (account.currentAmount || 0), 0
        );
        
        emergencyFundRatio = totalEmergencyFunds / monthlyExpenses;
        
        console.log('💡 Emergency Fund Calculation:', {
          monthlyExpenses,
          emergencyFunds: emergencyFunds.length,
          totalEmergencyFunds,
          monthsCovered: emergencyFundRatio.toFixed(1)
        });
      }
      
      // Calculate overall goal progress
      const goalProgress = totalTarget > 0 
        ? (totalSavings / totalTarget) * 100 
        : 0;
      
      // Calculate ratio of savings to income using real income data
      let monthlyIncome = 0;
      let savingsToIncomeRatio = 0;
      
      try {
        // Get real income data
        const incomeResponse = await axios.get(`${API_URL}/api/cashflow/income`, this.getAuthHeader());
        if (incomeResponse.data && Array.isArray(incomeResponse.data)) {
          monthlyIncome = incomeResponse.data.reduce((sum, income) => {
            return sum + (income.monthlyAmount || 0);
          }, 0);
          
          if (monthlyIncome > 0 && monthlySavingsRate > 0) {
            savingsToIncomeRatio = monthlySavingsRate / monthlyIncome;
          }
          
          console.log('💰 Savings to Income Calculation:', {
            monthlyIncome,
            monthlySavingsRate,
            savingsRate: (savingsToIncomeRatio * 100).toFixed(1) + '%'
          });
        }
      } catch (error) {
        console.log('Could not fetch income data for savings ratio:', error.message);
      }
      
      // Calculate projected annual growth
      const projectedAnnualGrowth = (totalSavings * (annualReturnRate / 100)) + 
        (monthlySavingsRate * 12);
      
      // Calculate YTD contributions
      // For this, we'd need to sum up all contributions made this year
      // Since we don't have that data, we'll estimate
      const ytdContributions = monthlySavingsRate * new Date().getMonth();
      
      const savingsStats = {
        totalSavings,
        monthlySavingsRate,
        annualReturnRate,
        emergencyFundRatio,
        goalProgress,
        savingsToIncomeRatio,
        projectedAnnualGrowth,
        ytdContributions
      };
      
      // Update mode-specific cache
      if (isPartnerMode) {
        cache.partnerSavingsStats = savingsStats;
      } else {
        cache.savingsStats = savingsStats;
      }
      
      return savingsStats;
    } catch (error) {
      console.error('Error fetching savings statistics:', error);
      
      // If we have cached stats, return them even if expired
      const fallbackStats = isPartnerMode ? cache.partnerSavingsStats : cache.savingsStats;
      if (fallbackStats) {
        console.log(`Using expired cached savings stats (${isPartnerMode ? 'partner' : 'personal'})`);
        return fallbackStats;
      }
      
      // Return default values as a last resort
      return {
        totalSavings: 0,
        monthlySavingsRate: 0,
        annualReturnRate: 0,
        emergencyFundRatio: 0,
        goalProgress: 0,
        savingsToIncomeRatio: 0,
        projectedAnnualGrowth: 0,
        ytdContributions: 0
      };
    }
  }
};

export default savingsService; 